import { HTMLAttributes, useRef } from 'react'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'
import Link from 'next/link'

import Container from '@/components/base/Container'
import Grid from '@/components/base/Grid'
import Image from '@/components/base/Image'
import Typography from '@/components/base/Typography'
import { useLenis } from '@/contexts/lenis'
import { useLoaderStore } from '@/store/loader'
import { isTouch } from '@/utils/device'

import { cn } from '@/lib/utils'
import { WPImage, WPPageData } from '@/types'

export type PostGridProps = HTMLAttributes<HTMLElement> &
  Partial<{
    items: {
      featured_image: WPImage
      link: string
      title: {
        rendered: string
      }
    }[]
    stickyAnimation: boolean
    theme: WPPageData['acf']['theme']
  }>

const PostGrid = ({
  items,
  theme = 'light',
  className,
  stickyAnimation = false,
  ...props
}: PostGridProps) => {
  const wrapper = useRef<HTMLDivElement>(null!)
  const loaded = useLoaderStore((state) => state.pageLoaded)
  const { lenis } = useLenis()

  useGSAP(
    (context) => {
      if (!lenis || !loaded) return
      const selector = context?.selector || gsap.utils.selector(wrapper)
      const items = selector('[data-motion-scroll-img]')
      const pinnedElement = document.querySelector('[data-hero]')
      let pinTrigger: ScrollTrigger | undefined

      if (stickyAnimation && pinnedElement) {
        const tlHero = gsap.timeline({}).fromTo(
          pinnedElement,
          {
            autoAlpha: 1
          },
          { autoAlpha: 0 }
        )
        pinTrigger = ScrollTrigger.create({
          trigger: wrapper.current,
          animation: tlHero,
          scrub: true,
          pin: pinnedElement,
          pinType: 'fixed',
          pinSpacing: false,
          start: isTouch ? 'top bottom-=26px' : 'top bottom+=112px',
          end: 'top top',
          invalidateOnRefresh: true
        })
      }

      const timeline = gsap.timeline({
        scrollTrigger: {
          trigger: wrapper.current,
          invalidateOnRefresh: true,
          scrub: 0.5,
          start: 'top 90%',
          end: 'top 20%'
        }
      })

      items.forEach((item: HTMLElement, index: number) => {
        timeline.fromTo(
          item,
          {
            y: 60 * index
          },
          {
            y: 0,
            duration: 4,
            ease: 'linear'
          },
          '<  '
        )
      })
      return () => {
        timeline.kill()
        if (pinTrigger) pinTrigger.kill()
      }
    },
    {
      dependencies: [lenis, loaded],
      scope: wrapper,
      revertOnUpdate: true
    }
  )

  return items && items?.length > 0 ? (
    <section
      ref={wrapper}
      {...props}
      className={cn([
        'z-30',
        {
          'bg-transparent': theme === 'light',
          'bg-dark-600': theme === 'dark'
        },
        className
      ])}
    >
      <Container>
        <Grid className="gap-y-[11.2rem]">
          {items?.map((item, index) => (
            <div
              key={index}
              className={cn(['col-span-4', 'md:col-span-3', 'lg:col-span-3'])}
              data-motion-scroll-img
            >
              <Link
                href={item.link}
                className="group flex flex-col gap-[1.6rem]"
                scroll={false}
              >
                {item.featured_image ? (
                  <div className="aspect-h-4 aspect-w-3 overflow-hidden">
                    <Image
                      source={item.featured_image}
                      size="large"
                      fit="cover"
                      className="transition-transform duration-700 ease-out group-hover:scale-[1.02]"
                    />
                  </div>
                ) : null}
                {item.title.rendered ? (
                  <Typography
                    variant="h3"
                    component="h3"
                    className="!font-normal text-dark-500"
                  >
                    {item.title.rendered}
                  </Typography>
                ) : null}
              </Link>
            </div>
          ))}
        </Grid>
      </Container>
    </section>
  ) : null
}

export default PostGrid
